<template>
  <div class="notifications__wrapper">
    <h2 class="notifications__title subtitle-3">Notifications</h2>

    <a-visibility show :on="[$breakpoint.mobile]">
      <div class="notifications__section-delimiter" />
    </a-visibility>

    <a-spinner
      v-if="inProgress"
      disable-transition
      class="notifications__spinner"
    />
    <template v-else-if="!isEmptyNotifications">
      <div
        v-for="section in sectionsWithContent"
        :key="section"
        class="notifications__section__wrapper"
      >
        <a-notification-section
          v-for="notification in notifications[section]"
          :key="notification.Id"
          :notification="notification"
          :section="section"
          class="notifications__message"
          @delete="deleteNotification"
        />
      </div>
    </template>

    <div
      v-else
      class="notifications__empty-content text-primary-navigation text-center"
    >
      There are no notifications
    </div>
    <div
      v-if="!inProgress && !isEmptyNotifications"
      class="notifications__footer text-medium bold"
    >
      <a-link :to="linkToNotificationPage" class="notifications__see-all-link">
        See all
      </a-link>
      <button class="notifications__button" @click.stop="clearAllNotifications">
        Clear all
      </button>
    </div>
  </div>
</template>

<script>
import mixins from '@/utils/mixins'
import { ROUTE_NAME } from 'enums/routes'
import ANotificationSection, {
  NOTIFICATION_SECTION
} from './ANotificationSection'
import ASpinner from 'shared/ASpinner'
import { mapActions } from 'vuex'

const NOTIFICATION_SECTIONS = Object.values(NOTIFICATION_SECTION)

export const SECTION_NAME = {
  [NOTIFICATION_SECTION.BUSINESS]: 'Business',
  [NOTIFICATION_SECTION.ANNOUNCEMENT]: 'Announcement',
  [NOTIFICATION_SECTION.NEWS]: 'News'
}

export default {
  name: 'ANotifications',
  mixins: [mixins.urlGenerators],
  inheritAttrs: false,
  components: { ASpinner, ANotificationSection },
  data() {
    return {
      notifications: {},
      inProgress: false,
      SECTION_NAME,
      linkToNotificationPage: {
        name: ROUTE_NAME.PERSONAL_PROFILE_NOTIFICATIONS
      }
    }
  },
  computed: {
    sectionsWithContent() {
      return NOTIFICATION_SECTIONS.filter(
        section => this.notifications[section].length
      )
    },
    isEmptyNotifications() {
      return Object.values(this.notifications).every(
        section => section.length === 0
      )
    }
  },
  methods: {
    ...mapActions({
      requestDeleteBellIconItem: 'notifications/requestDeleteBellIconItem',
      requestClearAllBellIcon: 'notifications/requestClearAllBellIcon',
      requestBellIconItems: 'notifications/requestBellIconItems',
      requestMarkReadBellIcon: 'notifications/requestMarkReadBellIcon'
    }),
    async deleteNotification(payload) {
      try {
        this.inProgress = true
        await this.requestDeleteBellIconItem({ payload })
        await this.requestNotifications()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    async clearAllNotifications() {
      try {
        this.inProgress = true
        await this.requestClearAllBellIcon()
        await this.requestNotifications()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    async requestNotifications() {
      try {
        this.inProgress = true
        this.notifications = await this.requestBellIconItems()
        await this.$nextTick()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    }
  },
  mounted() {
    this.requestNotifications()
    this.requestMarkReadBellIcon()
  }
}
</script>

<style lang="scss" scoped>
.notifications__wrapper {
  .notifications__title {
    text-align: left;
  }

  .notifications__section-delimiter {
    height: 1px;
    margin: 24px 0;
    background: $c--gray-dark;
    content: '';
  }

  .notifications__spinner {
    margin-top: 27px;
  }

  .notifications__section-title {
    margin-top: 20px;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: $c--white;
  }

  .notifications__empty-content {
    padding: 20px 0;
  }

  .notifications__footer {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
  }

  .notifications__see-all-link {
    color: $c--white;

    &:hover {
      color: $c--main;
    }
  }

  .notifications__button {
    color: $c--white;

    &:hover {
      color: $c--main;
    }
  }
}
</style>
